import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { Button, Dropdown, Input, Loader } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import InfiniteScroll from 'components/InfiniteScroll';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import SelectionItem from './components/SelectionItem';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import useArrayRef from 'hooks/useArrayRef';
import XcircleIcon from 'icons/Xcircle.icon';
import SearchIcon from 'icons/Search.icon';
import utils, {
  isArrowDownPressed,
  isArrowUpPressed,
  isEnterPressed,
} from 'utils';
import {
  getSearchStringFromOption,
  arrayFromValues,
  checkIsSelected,
  isObjectArray,
} from './helpers';
import { useTranslation } from 'react-i18next';

import './MultiSelect.styles.scss';

export type MultiOptionValue = string | number | Record<string, any>;
export type MultiOption<T = MultiOptionValue> =
  | {
      label: React.ReactNode;
      value: T;
    }
  | {
      label: {
        text: React.ReactNode;
        icon: React.ReactNode;
      };
      searchValue: string;
      value: T;
    };

export type MultiSelectProps<T extends MultiOptionValue> = {
  className?: string;
  placeholder?: string;
  options?: Array<MultiOption<T>>;
  value: Array<T> | Array<MultiOption<T>>;
  searchable?: boolean;
  async?: boolean;
  hasError?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  portalClassName?: string;
  emptySearchTitle?: string;
  noDataTitle: string;
  onScroll?: () => void;
  onSearchChange?: (value: string) => void;
  onChange: (selected: Array<MultiOption<T>>) => void;
  hideSelected: boolean;
} & Pick<DropdownProps, 'renderAsPortal' | 'portalClassName'>;

function MultiSelect<T extends MultiOptionValue>(props: MultiSelectProps<T>) {
  const { t } = useTranslation();
  const {
    className,
    options: pOptions,
    value: pValue,
    async = false,
    hasError = false,
    searchable = true,
    icon = SearchIcon,
    placeholder = t('multiSelectDefaultPlaceholder'),
    loading,
    portalClassName,
    emptySearchTitle,
    noDataTitle,
    renderAsPortal,
    onScroll,
    onChange,
    onSearchChange,
    hideSelected,
  } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const [search, setSearch] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const [selectedOptions, setSelectedOptions] = useState<Array<MultiOption<T>>>(
    pValue
      ? isObjectArray<MultiOption<T>>(pValue as MultiOption<T>[])
        ? (pValue as Array<MultiOption<T>>)
        : arrayFromValues(
            pValue as Array<any>,
            pOptions as Array<MultiOption<T>>,
          )
      : [],
  );

  useEffect(() => {
    setSelectedOptions(
      pValue
        ? isObjectArray<MultiOption<T>>(pValue as MultiOption<T>[])
          ? (pValue as Array<MultiOption<T>>)
          : arrayFromValues(
              pValue as Array<any>,
              pOptions as Array<MultiOption<T>>,
            )
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pValue]);

  const takeHoverInConsideration = useRef<boolean>(false);

  const classes = useMemo(
    () =>
      classNames(
        'ncoded-multi-select',
        {
          'ncoded-multi-select--open': dropdown?.isOpen,
          'ncoded-multi-select--close': !dropdown?.isOpen,
          'ncoded-multi-select--error': hasError,
        },
        className,
      ),
    [className, dropdown?.isOpen, hasError],
  );

  const searchOptions = useMemo(() => {
    if (!searchable || async) return null;

    return pOptions.filter((option) => {
      const searchValue = getSearchStringFromOption(option);
      return RegExp(
        ` ${search.replace(/[*+[\]-]/i, '').trim()} `
          .split('')
          .join('.*')
          .trim(),
        'gi',
      ).test(searchValue);
    });
  }, [searchable, async, pOptions, search]);

  const finalOptions = useMemo(
    () => (async || !searchable ? pOptions : searchOptions) || [],
    [async, pOptions, searchOptions, searchable],
  );

  const optionsRef = useArrayRef<HTMLDivElement>(finalOptions.length);

  const handleOnDeleteSelection = useCallback((value: T) => {
    setSelectedOptions((oldOpts) =>
      oldOpts?.filter((opt) => opt.value !== value),
    );
  }, []);

  const handleOnAddSelection = useCallback((selection: MultiOption<T>) => {
    setSelectedOptions((oldOpts) => {
      const alreadyExists = oldOpts.find(
        (opt) => opt.value === selection.value,
      );

      return alreadyExists
        ? oldOpts.filter((opt) => opt.value !== selection.value)
        : [...oldOpts, selection];
    });
  }, []);

  const onOptionKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (activeIndex >= 0 && isEnterPressed(event)) {
        handleOnAddSelection(pOptions[activeIndex]);
        return;
      }

      const onArrow = (first: number, last: number) => {
        event.preventDefault();

        if (activeIndex === -1) {
          optionsRef[first].current?.focus();
          return;
        }

        if (activeIndex === last) {
          optionsRef[first].current?.focus();
          return;
        }

        optionsRef[activeIndex + (first < last ? 1 : -1)].current?.focus();
        setActiveIndex(activeIndex + (first < last ? 1 : -1));
        return;
      };

      if (isArrowDownPressed(event)) {
        return onArrow(0, finalOptions.length - 1);
      }

      if (isArrowUpPressed(event)) {
        return onArrow(finalOptions.length - 1, 0);
      }
    },
    [
      activeIndex,
      finalOptions.length,
      handleOnAddSelection,
      optionsRef,
      pOptions,
    ],
  );

  useEffectSkipFirst(() => {
    onChange?.(selectedOptions);
  }, [onChange, selectedOptions]);

  return (
    <Dropdown
      portalClassName={portalClassName}
      className={classes}
      closeWithJS
      ref={dropdownRef}
      onKeyDown={(ev) => onOptionKeyDown(ev)}
      renderAsPortal={renderAsPortal}
      trigger={
        <div className="ncoded-multi-select__value">
          {icon && utils.renderIcon(icon)}
          <div className="ncoded-multi-select__value__options">
            {searchable ? (
              <Input
                className="ncoded-multi-select__input"
                value={search}
                placeholder={placeholder}
                onClick={(e) => {
                  if (search && dropdown.isOpen) e.stopPropagation();
                }}
                onChange={(e) => {
                  setSearch(e.target.value);
                  onSearchChange(e.target.value);
                }}
              />
            ) : (
              !selectedOptions.length && (
                <span className="ncoded-multi-select__placeholder">
                  {placeholder}
                </span>
              )
            )}
            {selectedOptions?.map((opt, index) => (
              <SelectionItem
                key={index}
                label={opt?.label as string}
                value={opt.value}
                onDelete={handleOnDeleteSelection}
              />
            ))}
          </div>
          {loading ? (
            <Loader
              className="ncoded-multi-select__loader"
              size="small"
              inline
            />
          ) : (
            selectedOptions.length > 0 && (
              <Button
                variant="link"
                className="delete"
                aria-label="Remove all"
                icon={XcircleIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOptions([]);
                }}
              />
            )
          )}
        </div>
      }
    >
      <InfiniteScroll
        role="listbox"
        localScroll
        className="ncoded-multi-select__options"
        onScroll={onScroll}
        onMouseLeave={() => setActiveIndex(-1)}
        onMouseMove={() => (takeHoverInConsideration.current = true)}
      >
        {finalOptions.map((opt, index) => {
          const isSelected = checkIsSelected(selectedOptions, opt.value);

          return (
            (!hideSelected || !isSelected) && (
              <div
                key={
                  'searchValue' in opt ? opt.searchValue : (opt.value as string)
                }
                tabIndex={-1}
                role="option"
                aria-selected={isSelected}
                className={classNames('ncoded-multi-select__option', {
                  'ncoded-multi-select__option--selected': isSelected,
                })}
                onClick={() => handleOnAddSelection(opt)}
                onMouseEnter={() => {
                  if (takeHoverInConsideration.current) {
                    takeHoverInConsideration.current = false;
                    setActiveIndex(index);
                  }
                }}
                onFocus={() => setActiveIndex(index)}
                ref={optionsRef[index]}
              >
                {'searchValue' in opt ? (
                  <>
                    {opt.label.icon}
                    {opt.label.text}
                  </>
                ) : (
                  opt.label
                )}
              </div>
            )
          );
        })}

        {!finalOptions.length && loading && !search && (
          <div className="ncoded-multi-select__empty">{t('isLoading')}</div>
        )}

        {!finalOptions.length && search && (
          <div className="ncoded-multi-select__empty">
            {emptySearchTitle} <span>{search}</span>
          </div>
        )}

        {!finalOptions.length && !search && !loading && (
          <div className="ncoded-multi-select__empty">{noDataTitle}</div>
        )}
      </InfiniteScroll>
    </Dropdown>
  );
}

export default MultiSelect as <T extends MultiOptionValue>(
  props: MultiSelectProps<T>,
) => React.ReactElement;
